// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL CÓDIGO DE UN SOLO USO DEL PORTAL DEL PACIENTE: QUE NO SE BORRE, QUE SE PUEDA PEGAR Y QUE NO SE PIERDA
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  EL FALLO (3-oct-2026), con las palabras del dueño: «al querer confirmar el móvil de acceso, si pongo el código
//  OTP que me envían y después pongo la contraseña de mi portal, se borra el código OTP».
//
//  QUÉ LO CAUSABA. El código iba en una casilla de texto SIN nombre ni `autocomplete`, justo ANTES de la contraseña.
//  Para el gestor de contraseñas del móvil o del navegador (el llavero del iPhone, el de Google en Android, el de
//  Chrome) la casilla de texto que va delante de una contraseña es la del USUARIO. Al elegir la contraseña guardada
//  rellena las dos: la contraseña en su sitio y el usuario —el correo o el teléfono con el que se entra— en la casilla
//  del código. Y esa casilla se queda solo con las cifras: de un correo no queda nada (el código se BORRA) y de un
//  teléfono quedan nueve cifras (el código CAMBIA). React no tenía nada que ver: escribir la contraseña a mano no
//  borraba nada; la borraba el relleno automático, que es justo lo que usa quien no se sabe la contraseña de memoria.
//
//  EL ARREGLO, EN TRES CAPAS (cada navegador hace caso a una; juntas valen en todos):
//   1. La casilla del código dice lo que es: `autocomplete="one-time-code"` y un nombre propio (CAMPO_CODIGO). Los
//      gestores ya no la toman por el usuario, y el iPhone ofrece encima del teclado el código que llega por SMS.
//   2. En los formularios con contraseña va una casilla OCULTA de usuario (`usuarioOculto`): el gestor pone su usuario
//      ahí, y además guarda o actualiza la contraseña con el usuario bueno (no con el código).
//   3. `codigoTecleado`: lo que no es un código no pisa el que ya hay. Un correo o un teléfono metidos por el
//      navegador se ignoran; un código pegado con espacio («123 456») o dentro del mensaje de WhatsApp entero
//      («🔐 *123456* es tu código…») se recoge solo.
//
//  Y LO QUE SE ESCRIBIÓ NO SE PIERDE AL SALIR A WHATSAPP. Para leer el código hay que salir a WhatsApp, y un móvil
//  con poca memoria recarga la página al volver: se quedaba otra vez en «escribe tu teléfono» y el código que acababa
//  de llegar ya no servía para nada. `recordarPaso` guarda en ESTA pestaña (sessionStorage, nunca en el servidor) el
//  paso en el que estaba, como mucho 10 minutos —lo que dura el código—. NUNCA se guardan el código ni la contraseña.
//
//  La guardia: backend/routes/portal-codigo-no-se-borra.test.js (las pantallas ejecutadas, con el gestor de
//  contraseñas que rellena como los de verdad).

export const CIFRAS_CODIGO = 6

// Lo que separa las dos mitades de un código cuando se escribe en dos grupos: espacio, guion, punto o el espacio duro.
const SEPARADOR = /^[ .\- ]$/

// El código que queda en la casilla después de lo que ha llegado (`nuevo`), sabiendo el que había (`anterior`).
// Sin «lookbehind» en la expresión regular, a propósito: un iPhone con iOS anterior al 16.4 no lo entiende y no
// cargaría ni la pantalla de entrar.
export function codigoTecleado(nuevo, anterior = '') {
  const s = String(nuevo == null ? '' : nuevo)
  const antes = String(anterior == null ? '' : anterior)
  // Lo normal: tecleando o borrando cifra a cifra.
  if (/^\d{0,6}$/.test(s)) return s
  // Pegado o rellenado: se buscan los NÚMEROS que trae (cifras unidas por un separador suelto cuentan como un solo
  // número, que es como se escribe un teléfono: «+34 612 345 678» es UN número de 11 cifras, no un código).
  const trozos = s.match(/\d+|\D+/g) || []
  const numeros = []
  let actual = null
  for (let i = 0; i < trozos.length; i++) {
    const t = trozos[i]
    if (/^\d/.test(t)) {
      if (actual) actual.push(t)
      else { actual = [t]; numeros.push(actual) }
    } else if (!(actual && SEPARADOR.test(t) && /^\d/.test(trozos[i + 1] || ''))) {
      actual = null
    }
  }
  // Es un código si son 6 cifras de un tirón o dos grupos de 3. Y tiene que haber UNO: si hay dos, no se adivina.
  const codigos = numeros
    .filter((n) => (n.length === 1 && n[0].length === CIFRAS_CODIGO) || (n.length === 2 && n[0].length === 3 && n[1].length === 3))
    .map((n) => n.join(''))
  if (codigos.length === 1) return codigos[0]
  // Lo demás —un correo, un teléfono, letras, una séptima cifra— no es un código: el que hubiera se queda.
  return antes
}

// Los atributos de TODA casilla de código del portal. Se esparcen con {...CAMPO_CODIGO} y después van `value`,
// `onChange` y el estilo de cada pantalla. Sin `maxLength` a propósito: recortaba lo pegado ANTES de que llegara
// aquí («123 456» se quedaba en «123 45»), y de que no pase de 6 cifras ya se encarga `codigoTecleado`.
export const CAMPO_CODIGO = {
  type: 'text',
  name: 'codigo',
  autoComplete: 'one-time-code',
  inputMode: 'numeric',
  pattern: '[0-9]*',          // el teclado de solo números también en los iPhone viejos
  autoCorrect: 'off',
  autoCapitalize: 'none',
  spellCheck: false,
}

// La casilla OCULTA del usuario de la cuenta, para los formularios con contraseña que no enseñan el usuario. No es
// de solo lectura a propósito: si un gestor de contraseñas quiere escribir su usuario en algún sitio, que sea aquí.
// Sin `value` controlado: lo que ponga el gestor da igual, no se lee nunca.
export function usuarioOculto(usuario) {
  return {
    type: 'text',
    name: 'username',
    autoComplete: 'username',
    defaultValue: usuario ? String(usuario) : '',
    hidden: true,
    tabIndex: -1,
    'aria-hidden': 'true',
    'data-usuario-oculto': 'si',
  }
}

// ── REENVIAR, CON SU ESPERA A LA VISTA ──────────────────────────────────────────────────────────
// El servidor deja pedir 4 códigos cada 10 minutos. Un «Reenviar» que se pulsa tres veces seguidas porque no se ve
// que haya pasado nada gasta los cuatro y deja a la persona fuera un rato: se espera un minuto, y se ve cuánto falta.
export const ESPERA_REENVIO_S = 60
export function segundosParaReenviar(enviadoEn, ahora = Date.now(), espera = ESPERA_REENVIO_S) {
  const t = Number(enviadoEn) || 0
  if (!t) return 0
  return Math.max(0, Math.ceil((t + espera * 1000 - Number(ahora)) / 1000))
}

// ── EL PASO EN EL QUE SE ESTABA, EN ESTA PESTAÑA ────────────────────────────────────────────────
const PREFIJO = 'portal_paso_'
const MINUTOS_CODIGO = 10    // lo que dura el código en el servidor
function almacen() {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null } catch (_) { return null }
}
export function recordarPaso(clave, datos, { minutos = MINUTOS_CODIGO, ahora = Date.now() } = {}) {
  const a = almacen()
  if (!a) return
  try { a.setItem(PREFIJO + clave, JSON.stringify({ hasta: Number(ahora) + minutos * 60000, datos })) } catch (_) { /* sin almacenamiento: no se recuerda, y ya está */ }
}
export function pasoRecordado(clave, ahora = Date.now()) {
  const a = almacen()
  if (!a) return null
  try {
    const crudo = a.getItem(PREFIJO + clave)
    if (!crudo) return null
    const o = JSON.parse(crudo)
    if (!o || !(Number(o.hasta) > Number(ahora)) || !o.datos) { a.removeItem(PREFIJO + clave); return null }
    return o.datos
  } catch (_) { return null }
}
export function olvidarPaso(clave) {
  const a = almacen()
  if (!a) return
  try { a.removeItem(PREFIJO + clave) } catch (_) { /* nada que olvidar */ }
}
